import { z } from 'zod';
// Meta content-plan schemas (phase 3 of the meta-cm roadmap).
//
// 15- or 30-day rolling content schedule. Mag builds it from the
// brand brain (voice + visual patterns mag learned in phase 1 + 1b)
// + the most recent engagement digest (phase 2's best-time windows,
// format-mix winners) + the founder's posting cadence preference.
//
// Each slot is a date + time-window + format + pillar + concept.
// The founder reviews + edits before scheduling kicks in (phase 5).
//
// Mag adapts the plan when:
//   - new engagement digest signal contradicts an earlier
//     assumption (mid-week regeneration)
//   - founder asks for a direction change via chat ("push more
//     product launches next week", phase 3b)
//   - a slot's date passes without a post (skip and re-plan)
export const contentPillarSchema = z.object({
    /** Short label, lowercase. "product education", "behind the
     *  scenes", "founder POV", "social proof", "ICP pain". The
     *  agent picks 3-5 pillars based on the brand brain — these
     *  are the recurring themes that anchor the plan. */
    label: z.string().min(1).max(60),
    /** One sentence on what this pillar covers + why mag picked it.
     *  Lowercase, mag voice. */
    description: z.string().min(1).max(280),
    /** Share of slots in the plan that this pillar should own, 0-100.
     *  The agent's sum across pillars equals ~100. */
    weightPercent: z.number().int().min(0).max(100),
});
export const contentFormatSchema = z.enum(['static_image', 'carousel', 'reel', 'video']);
/** Per-slot draft (phase 4). The slot itself stakes out the
 *  what/when/format; the draft fills in the actual caption +
 *  visual prompt mag would publish. Optional on the slot — slots
 *  start un-drafted and get filled in either by founder click
 *  (manual) or by mag's autopilot (phase 5). */
export const contentPlanSlotDraftSchema = z.object({
    /** Mag's draft caption — lowercase, mag-voice, ≤2200 chars
     *  matches IG's caption ceiling so the founder doesn't write
     *  something that gets silently truncated. */
    caption: z.string().min(1).max(2200),
    /** Mag's prose description of the visual. On static_image /
     *  carousel slots this is what the image should literally show
     *  (subject, composition, lighting). On reel / video slots this
     *  is the visual + motion intent — describes what frames the
     *  viewer sees over the video's duration. Single field works for
     *  both because the render path picks the right pipeline based
     *  on slot.format, and the agent's format-aware guidance varies
     *  the brief's content accordingly. */
    imageBrief: z.string().min(1).max(800),
    /** Publicly-accessible URL once a static image visual is rendered
     *  (image / carousel formats). For reel / video slots see
     *  videoUrl below. Optional — founder can review + edit the
     *  caption + brief before any visual exists. */
    imageUrl: z.string().url().max(2048).optional(),
    /** s233.5 — publicly-accessible mp4 URL once a video visual is
     *  rendered (reel / video formats). Set by
     *  generateOrganicMetaSlotVideo. Mutually exclusive with imageUrl
     *  in practice — the slot's format gates which one gets stamped. */
    videoUrl: z.string().url().max(2048).optional(),
    /** s233.5 — render duration of the video in seconds. Mag picks
     *  via the template-fill agent (4-12 range, same as drop concepts).
     *  Surfaces in the dashboard so the founder can see "8s reel" +
     *  bump it via the per-asset override path (s233.4). Only set
     *  when videoUrl is set. */
    videoDurationSeconds: z.number().int().min(4).max(12).optional(),
    /** s233.5 — foreign key into creative_assets. The video render
     *  path produces a creative_asset row (via runCreativePipeline);
     *  this id is what Phase B v2 will join on to map
     *  (publishedPostId → IG insights) ↔ (creative_asset → duration).
     *  Also lets the dashboard deep-link to the asset's drawer for
     *  per-slide override + iteration. Set on video renders only. */
    creativeAssetId: z.string().uuid().optional(),
    /** ISO timestamp of the most recent draft. Surfaces in the UI
     *  as "drafted N min ago" + invalidates downstream caches when
     *  the founder re-runs. */
    draftedAt: z.string(),
    /** Cost of the LLM call that produced this draft (USD). Surfaces
     *  in the per-slot footer for transparency, same as the other
     *  agent surfaces. */
    costUsd: z.number().min(0),
    /** Set when the post has been published (phase 5). Mock + real
     *  paths share the same shape. */
    publishedAt: z.string().datetime().optional(),
    /** Platform-side post id once shipped (phase 5). Mock publishes
     *  use `mock_ig_<random>`; real uses the Graph media id. */
    publishedPostId: z.string().min(1).max(120).optional(),
    /** How it shipped — 'mock' or 'meta'. */
    publishedVia: z.enum(['mock', 'meta']).optional(),
    /** Set when the scheduler decided to skip this slot rather than
     *  publish it (phase 5b). Mutually exclusive with publishedAt —
     *  a skipped slot can still be force-published by the founder via
     *  the publish-now button, at which point publishedAt is set and
     *  skippedAt remains as an audit trail of what mag wanted. */
    skippedAt: z.string().datetime().optional(),
    /** Why mag skipped, in her voice. Lowercase, one sentence.
     *  ("i shipped 'X' less than 6h ago — letting it breathe.") */
    skipReason: z.string().min(1).max(280).optional(),
    /** Phase A2 — autonomous rewrite audit. Stamped when mag rewrote
     *  this slot's caption + image brief in response to the digest's
     *  underperformer hypothesis flagging the slot's format/pillar
     *  as flat. previousDraft holds the pre-rewrite copy so the
     *  founder can hit "revert" and restore it. */
    rewrittenAt: z.string().datetime().optional(),
    /** Why mag rewrote — receipts in her voice. ≤280 chars,
     *  lowercase. ("your last 4 carousels averaged 2.1% engagement;
     *  i rewrote this one to lean into the founder-pov hook that's
     *  pulling 6%.") */
    rewriteReason: z.string().min(1).max(280).optional(),
    /** Engagement-digest card id that triggered this rewrite. Per-
     *  digest dedup: the rewrite pass only fires for a slot once per
     *  digest, so subsequent ticks see this stamp and no-op. */
    rewriteSourceDigestId: z.string().uuid().optional(),
    /** Snapshot of the pre-rewrite draft. Founder revert restores
     *  these fields onto the slot's live draft + clears the rewrite
     *  audit. Only the caption + imageBrief + imageUrl carry over —
     *  publish fields (publishedAt etc.) can't co-exist with a rewrite
     *  since published slots don't get rewritten. */
    previousDraft: z
        .object({
        caption: z.string().min(1).max(2200),
        imageBrief: z.string().min(1).max(800),
        imageUrl: z.string().url().max(2048).optional(),
        videoUrl: z.string().url().max(2048).optional(),
        videoDurationSeconds: z.number().int().min(4).max(12).optional(),
        creativeAssetId: z.string().uuid().optional(),
    })
        .optional(),
});
/** One scheduled slot. The plan stakes out date/time/format/pillar/
 *  concept-hook; the optional `draft` carries the full per-post
 *  caption + visual prompt (phase 4). Phase 5 will wire publishing
 *  off these. */
export const contentPlanSlotSchema = z.object({
    /** Stable id for slot-level edits + dedup across re-runs. */
    slotId: z.string().min(1).max(120),
    /** ISO date (YYYY-MM-DD) in the brand's tz. */
    date: z.string(),
    /** Local-tz hour, 0-23. Single hour (not range) — mag picks the
     *  exact post time based on best-time-to-post windows. */
    hour: z.number().int().min(0).max(23),
    format: contentFormatSchema,
    /** Which pillar this slot belongs to. Free-form match against
     *  one of the pillars[]. label values — the renderer + revise
     *  agent join on label. */
    pillar: z.string().min(1).max(60),
    /** One-line hook concept — what this post leads with. Lowercase,
     *  mag voice. The full draft (caption + visual prompt) is built
     *  in phase 4. */
    concept: z.string().min(1).max(280),
    /** Why mag picked this concept for this slot — rationale grounded
     *  in the engagement signal or brand brain. ≤500 chars. */
    rationale: z.string().min(1).max(500),
    /** Per-slot draft (phase 4). Null when not yet drafted — slot is
     *  a stub. Populated by runMetaPostDraft (manual founder click)
     *  or phase 5's autopilot. */
    draft: contentPlanSlotDraftSchema.nullable().optional(),
    /** Phase 5c — adaptive reschedule. Stamped when the scheduler
     *  shifts this slot's hour because the most recent engagement
     *  digest's best-time windows didn't cover the original hour.
     *  Lives on the slot (not draft) so reschedule can also fire on
     *  pre-draft stubs. */
    rescheduledAt: z.string().datetime().optional(),
    /** Why mag rescheduled, in her voice. ≤280 chars, lowercase.
     *  ("your tue 9-11pm window pulls 2× the median — moved you from
     *  3pm to 21:00.") */
    rescheduleReason: z.string().min(1).max(280).optional(),
    /** Id of the engagement-digest feed-card that drove the
     *  reschedule. Used as a per-digest dedup so we don't re-shift
     *  the same slot on every 5-min tick — only when a NEW digest
     *  lands does this slot become eligible again. */
    rescheduleSourceDigestId: z.string().uuid().optional(),
});
/** The cadence the agent picked, stored on the card payload.
 *  Always concrete after generation — Mag decides these based on
 *  the engagement signal + brand pattern. */
export const contentPlanCadenceSchema = z.object({
    /** Plan length in days the agent committed to. */
    planDays: z.number().int().min(7).max(60),
    /** Posts per week the agent committed to. */
    postsPerWeek: z.number().int().min(1).max(14),
    /** First date the plan covers (YYYY-MM-DD, brand tz). */
    startDate: z.string(),
});
/** Optional founder hints passed to the agent. When unset, Mag picks
 *  the cadence herself based on the engagement signal + brand
 *  pattern. When set, she respects the hint. Surfaces as the API
 *  request body shape. */
export const contentPlanHintsSchema = z.object({
    /** "i want a 15-day plan instead of a month" / "i can only post
     *  once a week, keep it light." Both optional; either can be set
     *  independently. */
    planDays: z.number().int().min(7).max(60).optional(),
    postsPerWeek: z.number().int().min(1).max(14).optional(),
    /** s216-54h.69 — audience-aligned posting windows captured during
     *  channel intake. Each entry is one hour-of-day in brand tz
     *  (0-23) with a one-line rationale. The agent uses these as the
     *  default hour pool until the engagement digest builds enough
     *  history to take over. Empty / unset → fall back to the legacy
     *  generic-hours behavior. */
    postingWindows: z
        .array(z.object({
        hour: z.number().int().min(0).max(23),
        reason: z.string().min(1).max(140),
    }))
        .min(1)
        .max(3)
        .optional(),
    /** Founder-requested first-post date (brand-tz YYYY-MM-DD), captured
     *  during channel intake. The scheduler floors it against the
     *  approval-lead window. Unset → schedule as early as the lead allows
     *  (legacy behavior). */
    firstPostDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/u)
        .optional(),
    /** Specific weekdays the founder named during channel intake
     *  ("tue/thu/sat"). When set, the scheduler places slots ONLY on
     *  these days (deterministic placement in channel-content-plan.ts).
     *  Unset/empty → mag spaces the cadence herself (legacy behavior). */
    postingDays: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])).optional(),
});
/** One entry in the plan's revision history — written every time
 *  the founder asks mag to tweak the plan via chat (phase 3b). */
export const contentPlanRevisionSchema = z.object({
    /** ISO timestamp of the revision. */
    at: z.string(),
    /** The founder's instruction verbatim. ≤1000 chars matches the
     *  api input cap. */
    instruction: z.string().min(1).max(1000),
    /** Mag's one-sentence recap of what she changed + why. Lowercase,
     *  mag voice. ("dropped the friday reel — you said you're
     *  traveling. moved its slot to monday morning so the week still
     *  hits 4 posts.") */
    summary: z.string().min(1).max(500),
    /** Cost in USD for this revision. Same surface as other cards. */
    costUsd: z.number().min(0),
});
/** What the meta-content-plan agent produces. */
export const metaContentPlanOutputSchema = z.object({
    /** Mag's one-sentence summary of the plan she's drawing up. Goes
     *  into the feed-card title. */
    summary: z.string().min(1).max(280),
    /** Cadence the agent committed to. Mag picks these based on the
     *  signal — engagement digest's audience curve, the brand's
     *  observed posting cadence, format-mix winners. Founder hints
     *  (if any) constrained this choice but didn't dictate it. */
    cadence: contentPlanCadenceSchema,
    /** Why mag picked this cadence. One sentence, lowercase, mag
     *  voice. ("your overperformers cluster on tue/thu — i kept it
     *  to 2/week so each slot can earn its spot.") */
    cadenceRationale: z.string().min(1).max(280),
    /** 3-5 pillars that anchor this plan. The agent picks them from
     *  the brand brain + engagement signal. */
    pillars: z.array(contentPillarSchema).min(2).max(6),
    /** The scheduled slots, oldest-first. */
    slots: z.array(contentPlanSlotSchema).min(1).max(60),
    /** Mag's confidence on the plan, 0-10. Low when the engagement
     *  signal is sparse (new brand, no insights yet) or when she
     *  doesn't have a clear voice read to ground hooks against. */
    confidence: z.number().int().min(0).max(10),
    /** Audit log of revisions (phase 3b). Optional + empty on
     *  freshly-generated plans; one entry per founder chat-driven
     *  revision, newest-last. */
    revisionHistory: z.array(contentPlanRevisionSchema).optional(),
    /** Publish authority for this plan (phase 5).
     *   - 'manual' (default): the founder presses "publish now" per
     *     slot. Nothing ships without a click.
     *   - 'auto': the scheduler walks the plan every few minutes and
     *     publishes drafted+rendered slots when their (date, hour,
     *     brand-tz) window passes. Founder enables this when they
     *     trust mag enough to let the loop run.
     *  Optional + treated as 'manual' when absent so plans drafted
     *  before phase 5 stay safely opt-out. */
    publishMode: z.enum(['manual', 'auto']).optional(),
});
/** Feed card that lands when mag drafts (or re-drafts) a content
 *  plan. Lands status='open' so the founder explicitly acks +
 *  feeds the trust ledger. The plan's cadence (planDays,
 *  postsPerWeek) lives on `plan.cadence` — mag chose it. */
export const metaContentPlanCardPayloadSchema = z.object({
    kind: z.literal('meta_content_plan'),
    /** Founder hints (if any) passed when triggering — kept on the
     *  payload as an audit of what was requested vs. what mag chose
     *  (plan.cadence). Empty object when nothing was hinted. */
    hints: contentPlanHintsSchema,
    /** ISO timestamp the plan was generated. Helps the renderer
     *  show "drafted N hours ago" + invalidates the plan when slots
     *  pass without posting (handled in phase 5's adapter). */
    generatedAt: z.string(),
    /** The agent's output. */
    plan: metaContentPlanOutputSchema,
    /** Cost in USD for transparency. Same surface as the other
     *  cards. */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=meta-content-plan.js.map